import { FC } from 'react';
import classnames from 'classnames';

import { IconHelper } from '@shippypro/design-system-web/iconhelper';
import {
  InteractableIconButton,
  TooltipWrapperByREF,
} from '@shippypro/design-system-web';

interface ISetupStepButtonProps {
  step: string;
  title: string;
  description?: string;
  icon: string;
  isCompleted: boolean;
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
}

/**
 * The generic setup step button
 * It changes its state whether the step is completed or not
 *
 * @interface ISetupStepButtonProps
 * @author Fabio Mezzina <fabio.mezzina@shippypro.com>ƒ
 */
export const SetupStepButton: FC<ISetupStepButtonProps> = ({
  step,
  title,
  description,
  icon,
  isCompleted,
  onClick,
  disabled = false,
  tooltip = '',
}) => {
  return (
    <TooltipWrapperByREF
      content={tooltip}
      disabled={tooltip === ''}
      fullWidth={false}
    >
      <div
        onClick={onClick}
        className={classnames(
          'h-full flex items-center gap-[0.571rem] !px-4 !py-2 !bg-transparent !text-[#ae96eb]',
          'border !border-[#B6A2E8] rounded-xl',
          {
            'hover:!text-[#fff] hover:!border-white cursor-pointer': !disabled,
            'cursor-not-allowed': disabled,
          },
        )}
        data-test={`${step}-setup-step-btn`}
      >
        <InteractableIconButton
          icon={icon}
          className={classnames({
            'bg-white': !disabled,
            'bg-[#9D80E7] hover:!bg-[#9D80E7]': disabled,
          })}
        />
        <div className="flex flex-col !gap-1 lh-sm">
          <div
            className={classnames('flex items-end !gap-1 font-semibold', {
              'text-white': !disabled,
              'text-decoration-line-through': isCompleted,
            })}
          >
            {title}
            {isCompleted && (
              <IconHelper icon="IconSquareCheckFilled" fill="#28c76f" />
            )}
            {disabled && <IconHelper icon="IconLock" />}
          </div>
          {description && (
            <div className="flex !gap-1 text-[#f1edfc]">
              <span className="d-block text-sm">{description}</span>
              <IconHelper icon="IconExternalLink" />
            </div>
          )}
        </div>
      </div>
    </TooltipWrapperByREF>
  );
};
